import { supabase } from './supabase';
import { logError } from './api/error-logger';

// Add retry mechanism for bot operations
const withBotRetry = async <T>(operation: () => Promise<T>) => {
  let attempts = 0;
  const maxAttempts = 3;

  while (attempts < maxAttempts) {
    try {
      return await operation();
    } catch (error: any) {
      attempts++;
      if (attempts === maxAttempts) throw error;
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempts) * 1000));
    }
  }
};

// Start trading function with settings
export async function startTrading(userId: string, botId: string, settings?: any) {
  return withBotRetry(async () => {
    try {
      // First check if bot exists and is active
      const { data: bot, error: botError } = await supabase
        .from('trading_bots')
        .select('*')
        .eq('id', botId)
        .single();

      if (botError) {
        if (botError.code === 'PGRST116') {
          throw new Error('Bot not found or has been deleted');
        }
        throw botError;
      }

      if (!bot.is_active) {
        throw new Error('Bot is currently inactive');
      }

      // Check if user has exchange API set up
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('trading_account, trading_enabled')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      if (!profile?.trading_account?.api_key || !profile?.trading_enabled) {
        throw new Error('Please set up your exchange API key and enable trading in settings first');
      }

      // Get current bot status
      const { data: currentStatus } = await supabase
        .from('user_bot_status')
        .select('is_trading')
        .eq('user_id', userId)
        .eq('bot_id', botId)
        .single();

      // Save user's trading settings while preserving trading state
      if (settings) {
        const { error: settingsError } = await supabase
          .from('user_bot_status')
          .upsert({
            user_id: userId,
            bot_id: botId,
            is_trading: currentStatus?.is_trading || false, // Preserve current trading state
            is_selected: true,
            settings: {
              position_size: settings.position_size,
              selected_pairs: settings.selected_pairs
            },
            error_count: 0,
            last_error: null,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,bot_id'
          });

        if (settingsError) throw settingsError;

        // Save selected pairs
        if (settings.selected_pairs?.length > 0) {
          const pairs = settings.selected_pairs.map((symbol: string) => ({
            bot_id: botId,
            pair_symbol: symbol
          }));

          const { error: pairsError } = await supabase
            .from('bot_trading_pairs')
            .upsert(pairs);

          if (pairsError) throw pairsError;
        }
      } else {
        // Update bot status without settings
        const { error: statusError } = await supabase
          .from('user_bot_status')
          .upsert({
            user_id: userId,
            bot_id: botId,
            is_trading: currentStatus?.is_trading || false, // Preserve current trading state
            is_selected: true,
            error_count: 0,
            last_error: null,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,bot_id'
          });

        if (statusError) throw statusError;
      }

      return true;
    } catch (error: any) {
      await logError({
        bot_id: botId,
        user_id: userId,
        error_type: 'ENGINE',
        error_message: error.message || 'Failed to start trading',
        error_details: error
      });
      throw error;
    }
  });
}

// Stop trading function
export async function stopTrading(userId: string, botId: string) {
  return withBotRetry(async () => {
    try {
      // Update bot status but preserve settings
      const { error: statusError } = await supabase
        .from('user_bot_status')
        .update({ 
          is_trading: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('bot_id', botId);

      if (statusError) throw statusError;
      return true;
    } catch (error: any) {
      await logError({
        bot_id: botId,
        user_id: userId,
        error_type: 'ENGINE',
        error_message: error.message || 'Failed to stop trading',
        error_details: error
      });
      throw error;
    }
  });
}

// Remove bot function
export async function removeBot(userId: string, botId: string) {
  return withBotRetry(async () => {
    try {
      // First stop trading if active
      const { error: statusError } = await supabase
        .from('user_bot_status')
        .update({ 
          is_trading: false,
          is_selected: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('bot_id', botId);

      if (statusError) throw statusError;

      // Delete the selection record
      const { error: selectionError } = await supabase
        .from('user_selections')
        .delete()
        .eq('user_id', userId)
        .eq('bot_id', botId);

      if (selectionError) throw selectionError;

      // Clean up trading pairs
      const { error: pairsError } = await supabase
        .from('bot_trading_pairs')
        .delete()
        .eq('bot_id', botId);

      if (pairsError) throw pairsError;

      return true;
    } catch (error: any) {
      await logError({
        bot_id: botId,
        user_id: userId,
        error_type: 'ENGINE',
        error_message: error.message || 'Failed to remove bot',
        error_details: error
      });
      throw error;
    }
  });
}

// Load saved user settings
export async function loadUserSettings(userId: string, botId: string) {
  try {
    const { data, error } = await supabase
      .from('user_bot_status')
      .select('settings')
      .eq('user_id', userId)
      .eq('bot_id', botId)
      .single();

    if (error) throw error;
    return data?.settings || null;
  } catch (error) {
    console.error('Error loading user settings:', error);
    return null;
  }
}
